import {
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
  signal,
} from '@angular/core';
import type { Data } from '@angular/router';
import { ChallengeFlyAnimationComponent } from './challenge-fly-animation/challenge-fly-animation.component';
import { resolveChallengeLottiePath } from './challenge-lottie-path.util';
import { challengeTypeAliases as resolveChallengeTypeAliases } from './challenge-type.util';

@Injectable({ providedIn: 'root' })
export class ChallengeFlyRendererService {
  /** Fly Lottie box (matches previous large trail size). */
  private static readonly BOX_SIZE_PX = 480;
  public isGlowAnimationEnd =signal(false);
  /**
   * Banner-fly.json end anchors (normalized 0–1 in 1080 composition).
   * Icons land here inside the Lottie — must map this point onto the card img.
   */
  private static readonly LOTTIE_END_ANCHOR: Record<string, { x: number; y: number }> = {
    competitive: { x: 405 / 1080, y: 1034.583 / 1080 },
    pvp: { x: 405 / 1080, y: 1034.583 / 1080 },
    luck: { x: 675.25 / 1080, y: 1034.833 / 1080 },
    instant: { x: 675.25 / 1080, y: 1034.833 / 1080 },
    mastery: { x: 945.75 / 1080, y: 1034.083 / 1080 },
    endless: { x: 945.75 / 1080, y: 1034.083 / 1080 },
  };
  private static readonly LOTTIE_START_ANCHOR = { x: 540 / 1080, y: 58 / 1080 };
  private glowLottieUrl?: string;
  /** Fallback when card rect is unavailable. */
  private static readonly GLOW_FALLBACK_PX = 120;
  /** Keep glow inside card borders (no overshoot past width). */
  private static readonly GLOW_WIDTH_FIT = 0.98;
  private static readonly GLOW_MIN_PX = 48;
  private static readonly GLOW_MAX_PX = 280;
  private static readonly GLOW_PATH = resolveChallengeLottiePath(
    undefined,
    'glow.json'
  );

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private envInjector: EnvironmentInjector
  ) {}

  /**
   * Fly from profile → challenge card, then glow only if glowLottieUrl is provided.
   */
  async showFlyThenGlow(
    challengeType: string,
    flyLottieUrl: string,
    glowLottieUrl?: string,
    flyMs = 1400,
  ): Promise<void> {
    this.glowLottieUrl = glowLottieUrl;
    await this.show(challengeType, flyLottieUrl, flyMs);
  }

  /**
   * Fly Lottie from prize-box toward the card image.
   * End position maps the Lottie’s built-in land point onto the img center
   * (`data-challenge-fly-end`), so the trail lines up with that image.
   */
  show(challengeType: string, lottieUrl: string, durationMs = 1400): Promise<void> {
    return new Promise((resolve) => {
      if (!lottieUrl) {
        resolve();
        return;
      }

      const cmpRef = createComponent(ChallengeFlyAnimationComponent, {
        environmentInjector: this.envInjector,
        elementInjector: this.injector,
      });

      cmpRef.instance.lottieUrl = lottieUrl;
      this.appRef.attachView(cmpRef.hostView);
      cmpRef.changeDetectorRef.detectChanges();
      const hostEl = (cmpRef.hostView as Data).rootNodes[0] as HTMLElement;

      const originEl =
        document.querySelector<HTMLElement>('[data-challenge-fly-origin]') ||
        document.querySelector<HTMLElement>('.prize-box-entry') ||
        document.querySelector<HTMLElement>('[data-hud-icon="star_token"]');
      const targetEl = this.resolveFlyTargetImage(challengeType);

      const originPoint = this.getElementCenter(originEl) ?? {
        x: window.innerWidth * 0.72,
        y: 72,
      };
      const targetPoint =
        this.getImageContentCenter(targetEl) ??
        this.getElementCenter(targetEl);

      const boxSize = ChallengeFlyRendererService.BOX_SIZE_PX;
      const startAnchor = ChallengeFlyRendererService.LOTTIE_START_ANCHOR;
      const endAnchor = this.resolveLottieEndAnchor(challengeType);

      // Box top-left so the Lottie icon anchor sits on origin / img center.
      const startLeft = originPoint.x - startAnchor.x * boxSize;
      const startTop = originPoint.y - startAnchor.y * boxSize;
      const endLeft = targetPoint
        ? targetPoint.x - endAnchor.x * boxSize
        : startLeft;
      const endTop = targetPoint
        ? targetPoint.y - endAnchor.y * boxSize
        : startTop + 280;

      hostEl.style.position = 'fixed';
      hostEl.style.left = `${startLeft}px`;
      hostEl.style.top = `${startTop}px`;
      hostEl.style.width = `${boxSize}px`;
      hostEl.style.height = `${boxSize}px`;
      hostEl.style.opacity = '1';
      hostEl.style.transform = 'none';
      hostEl.style.transition = `left ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1), top ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${Math.min(320, durationMs)}ms ease-out`;
      hostEl.style.zIndex = '10000';
      hostEl.style.pointerEvents = 'none';
      hostEl.style.overflow = 'visible';
      hostEl.style.margin = '0';

      document.body.appendChild(hostEl);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          hostEl.style.left = `${endLeft}px`;
          hostEl.style.top = `${endTop}px`;
        });
      });

      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        hostEl.style.opacity = '0';
        if (this.glowLottieUrl) {
          this.showGlow(challengeType, 2000, this.glowLottieUrl);
        }
        try {
          this.appRef.detachView(cmpRef.hostView);
        } catch {
          // already detached
        }
        cmpRef.destroy();
        resolve();
      };

      setTimeout(finish, durationMs);
    });
  }

  /**
   * Fly end target (img reference) — first match wins:
   * 1. `[data-challenge-fly-end]` (exact / alias / case-insensitive)
   * 2. `img.card-img` inside `[data-challenge-icon]`
   * 3. `[data-challenge-icon]`
   * 4. `[data-challenge-anchor]`
   */
  private resolveFlyTargetImage(challengeType: string): HTMLElement | null {
    const aliases = this.challengeTypeAliases(challengeType);

    for (const key of aliases) {
      const tagged = document.querySelector<HTMLElement>(
        `[data-challenge-fly-end="${key}"]`
      );
      if (tagged) {
        return tagged;
      }
    }

    // Case-insensitive scan of tagged images.
    const allTagged = document.querySelectorAll<HTMLElement>('[data-challenge-fly-end]');
    for (const el of Array.from(allTagged)) {
      const v = (el.getAttribute('data-challenge-fly-end') || '').trim().toLowerCase();
      if (aliases.some((a) => a.toLowerCase() === v)) {
        return el;
      }
    }

    for (const key of aliases) {
      const iconWrap = document.querySelector<HTMLElement>(
        `[data-challenge-icon="${key}"]`
      );
      const img =
        iconWrap?.querySelector<HTMLElement>('img.card-img') ||
        iconWrap?.querySelector<HTMLElement>('img');
      if (img) {
        return img;
      }
      if (iconWrap) {
        return iconWrap;
      }
    }

    for (const key of aliases) {
      const anchor = document.querySelector<HTMLElement>(
        `[data-challenge-anchor="${key}"]`
      );
      if (anchor) {
        return anchor;
      }
    }

    return null;
  }

  private challengeTypeAliases(challengeType: string): string[] {
    return resolveChallengeTypeAliases(challengeType);
  }

  private resolveLottieEndAnchor(challengeType: string): { x: number; y: number } {
    const lower = (challengeType || '').trim().toLowerCase();
    return (
      ChallengeFlyRendererService.LOTTIE_END_ANCHOR[lower] ||
      ChallengeFlyRendererService.LOTTIE_END_ANCHOR.competitive
    );
  }

  private getElementCenter(
    el: HTMLElement | null | undefined
  ): { x: number; y: number } | null {
    if (!el) {
      return null;
    }
    const rect = el.getBoundingClientRect();
    if (!(rect.width > 0 || rect.height > 0)) {
      return null;
    }
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  /** Center of the painted image (object-fit: contain aware). */
  private getImageContentCenter(
    el: HTMLElement | null | undefined
  ): { x: number; y: number } | null {
    if (!el) {
      return null;
    }
    const rect = el.getBoundingClientRect();
    if (!(rect.width > 0 || rect.height > 0)) {
      return null;
    }

    if (el instanceof HTMLImageElement) {
      const nw = el.naturalWidth || 0;
      const nh = el.naturalHeight || 0;
      if (nw > 0 && nh > 0) {
        const scale = Math.min(rect.width / nw, rect.height / nh);
        const cw = nw * scale;
        const ch = nh * scale;
        return {
          x: rect.left + (rect.width - cw) / 2 + cw / 2,
          y: rect.top + (rect.height - ch) / 2 + ch / 2,
        };
      }
    }

    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  /** Play glow.json sized to the challenge card (responsive). */
  showGlow(
    challengeType: string,
    durationMs = 2000,
    lottieUrl?: string
  ): Promise<void> {
    const path = lottieUrl || ChallengeFlyRendererService.GLOW_PATH;
    return this.showAtAnchor(challengeType, path, durationMs);
  }

  /**
   * Play glow mounted inside the card (`[data-challenge-anchor]`),
   * sized from that card's live rect so it scales with the HUD.
   */
  showAtAnchor(
    challengeType: string,
    lottieUrl: string,
    durationMs = 2000,
    sizePx?: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const path = resolveChallengeLottiePath(lottieUrl, 'glow.json');
      if (!path) {
        resolve();
        return;
      }

      const marksGlowComplete =
        /glow/i.test(path) || /glow/i.test(String(lottieUrl || ''));
      if (marksGlowComplete) {
        this.isGlowAnimationEnd.set(false);
      }

      // Alias-aware: HUD may use pvp/competitive (etc.) while queue uses the other.
      const cardEl = this.queryByChallengeAttr('data-challenge-anchor', challengeType);
      const progressEl = this.queryByChallengeAttr(
        'data-challenge-progress',
        challengeType
      );
      const iconEl = this.queryByChallengeAttr('data-challenge-icon', challengeType);
      // Prefer card so glow matches the whole card column (img + progress).
      const mountParent = cardEl || progressEl || iconEl;
      if (!mountParent) {
        resolve();
        return;
      }

      const size = sizePx ?? this.resolveGlowSizePx(cardEl || mountParent);

      const cmpRef = createComponent(ChallengeFlyAnimationComponent, {
        environmentInjector: this.envInjector,
        elementInjector: this.injector,
      });

      // Canvas: glow.json embeds a PNG image layer.
      cmpRef.instance.configure({
        path,
        widthPx: size,
        heightPx: size,
        autoplay: true,
        renderer: 'canvas',
      });
      this.appRef.attachView(cmpRef.hostView);
      cmpRef.changeDetectorRef.detectChanges();
      const hostEl = (cmpRef.hostView as Data).rootNodes[0] as HTMLElement;

      const computedPos = getComputedStyle(mountParent).position;
      if (computedPos === 'static') {
        mountParent.style.position = 'relative';
      }

      hostEl.style.position = 'absolute';
      hostEl.style.left = '50%';
      hostEl.style.top = '50%';
      hostEl.style.width = `${size}px`;
      hostEl.style.height = `${size}px`;
      hostEl.style.maxWidth = '100%';
      hostEl.style.transform = 'translate(-50%, -50%)';
      hostEl.style.zIndex = '0';
      hostEl.style.pointerEvents = 'none';
      hostEl.style.opacity = '1';
      hostEl.style.overflow = 'hidden';
      hostEl.style.margin = '0';

      // Behind progress wipe / label (z-index 1–2 on those layers).
      mountParent.insertBefore(hostEl, mountParent.firstChild);
      cmpRef.changeDetectorRef.detectChanges();

      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        if (marksGlowComplete) {
          this.isGlowAnimationEnd.set(true);
        }
        settled = true;
        try {
          this.appRef.detachView(cmpRef.hostView);
        } catch {
          // already detached
        }
        cmpRef.destroy();
        resolve();
      };

      setTimeout(finish, durationMs);
    });
  }

  /** First element matching `attr` for any challenge-type alias. */
  private queryByChallengeAttr(
    attr: string,
    challengeType: string
  ): HTMLElement | null {
    for (const key of resolveChallengeTypeAliases(challengeType)) {
      const el = document.querySelector<HTMLElement>(`[${attr}="${key}"]`);
      if (el) {
        return el;
      }
    }
    const all = document.querySelectorAll<HTMLElement>(`[${attr}]`);
    const aliasesLower = resolveChallengeTypeAliases(challengeType).map((a) =>
      a.toLowerCase()
    );
    for (const el of Array.from(all)) {
      const v = (el.getAttribute(attr) || '').trim().toLowerCase();
      if (aliasesLower.includes(v)) {
        return el;
      }
    }
    return null;
  }

  /** Glow box = card width (never wider than card borders / divider). */
  private resolveGlowSizePx(cardOrMount: HTMLElement): number {
    const rect = cardOrMount.getBoundingClientRect();
    const width = rect.width;
    if (!(width > 0)) {
      return ChallengeFlyRendererService.GLOW_FALLBACK_PX;
    }
    // Fit inside card — height may match width (square Lottie) but width must not spill.
    const fitted = Math.floor(width * ChallengeFlyRendererService.GLOW_WIDTH_FIT);
    return Math.max(
      ChallengeFlyRendererService.GLOW_MIN_PX,
      Math.min(ChallengeFlyRendererService.GLOW_MAX_PX, fitted)
    );
  }
}
